import { z } from 'zod';
import { orderPODCopyStatusSchema, orderPODImageStatusSchema } from './order.pod.model';

//* overall
export const orderOverallStatusSchema = z.enum([
  'new',
  'accepted',
  'confirmed',
  'in_transit_pickup',
  'arrived_pickup',
  'in_transit_dropoff',
  'arrived_dropoff',
  'delivered',
  /**
   * Successful short-lifecycle terminal (e.g. Wathiqa waybill-only), and
   * auto-close target after idle. Distinct from `delivered` (full journey)
   * and `canceled` (aborted).
   */
  'closed',
  'canceled',
]);

export type OrderOverallStatus = z.infer<typeof orderOverallStatusSchema>;
/** An order in one of these is history: its snapshots stay as they were when it ended. */
export const TERMINAL_ORDER_STATUSES = ['delivered', 'closed', 'canceled'] as const satisfies readonly OrderOverallStatus[];
//* driver
export const orderDriverStatusSchema = z.enum([
  'not_assigned',
  'searching_for_driver',
  'unable_to_find_carrier',
  'accepted', // when a carrier accepts the order but not assigned a driver to it.
  'assigned', // this means we have a driver assigned to the order and everything is ready to go.
]);
export type OrderDriverStatus = z.infer<typeof orderDriverStatusSchema>;
//* Destination
export const orderDestinationStatusSchema = z.enum([
  'new',
  'upcoming', // coming next
  'in_transit',
  'arrived',
  'done',
  'canceled',
]);
export type OrderDestinationStatus = z.infer<typeof orderDestinationStatusSchema>;

export const orderStatusSchema = z.object({
  /** the overall order status */
  order: orderOverallStatusSchema,
  /** the driver status in the order, assigned/not assigned/etc */
  driver: orderDriverStatusSchema,
  /** the current destination status */
  destination: orderDestinationStatusSchema,
  /**
   * the status of the proof of delivery (POD) copy.
   * - `not_received` = means the driver/carrier has the original copy
   * - `received` = means we have the original copy of the POD in our office
   *
   * **Important Note**:
   *
   * If `settings.podCopy` contain `return_original_pod`, then the driver must return the POD original copy to our office.
   *
   */
  podCopy: orderPODCopyStatusSchema,
  
  /** the status of the proof of delivery (POD) images. 
   * 
   * - Complete it means all images are uploaded and complete.
   * - Not required it means the POD image is not required for this order.
   * - Pending it means the POD image is still not avalible for upload yet.
   * 
  */
  podImage: orderPODImageStatusSchema,
});
export type OrderStatus = z.infer<typeof orderStatusSchema>;

// this will be used for the API mainly
export const OrderDriverActionTypesSchema = z.enum([
  'book_order', // this come from the front end app (carrier app/dashboard) when they try to book the order.
  'journey_start',
  'in_transit',
  'arrived',
  'items_done',
  'rest_start',
  'rest_end',
  'delivered',
]);

export const OrderCustomerActionTypesSchema = z.enum([
  'confirm_order',
  'cancel_order',
  'return_order',
]);
export const SuperAdminActionTypesSchema = z.enum([
  'super_admin_canceled_order',
  'unable_to_find_carrier',
  'accept_and_publish_order', // accept the order and publish it to the carriers "look_for_driver"
  'publish_order', // publish the order to the carriers "looking for drivers"
  'accept_order', // super admin action
  /**
   * Successful short-lifecycle terminal (system/cron + optional admin).
   * Distinct from `delivered` (full journey) and `canceled` (aborted).
   */
  'close_order',
]);
export const OrderActionTypesSchema = z.enum([
  'look_for_driver',
  'ready_to_start_journey',
  ...OrderCustomerActionTypesSchema.options,
  ...OrderDriverActionTypesSchema.options,
  ...SuperAdminActionTypesSchema.options,
]);

export type OrderActionTypes = z.infer<typeof OrderActionTypesSchema>;
